/**
 * Admin · P&L: revenue, cost and profit of every call (GET /admin/pnl, see
 * pnl.py). Twilio's price comes from the Twilio API, the AI services' cost
 * from the usage the bot recorded on the call, and the revenue from what the
 * user paid per credit (Stripe).
 */
import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi, PnlCall, PnlReport, TranscriptMessage } from './adminApi';
import { TranscriptMessages } from './AdminUserDetail';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

const WINDOWS = [7, 30, 90, 365];
// Columns in the calls table, for a transcript row spanning all of them.
const TABLE_COLUMNS = 18;

/** Yen, with decimals only below ¥100 where they matter. */
export function yen(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const digits = Math.abs(value) < 100 ? 2 : 0;
  return `¥${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function fmtDuration(seconds: number): string {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

export function Profit({ value }: { value: number | null }) {
  if (value === null) return <span className="admin-muted">—</span>;
  return <span className={`answer-badge ${value >= 0 ? 'positive' : 'negative'}`}>{yen(value)}</span>;
}

function usageTitle(c: PnlCall): string | undefined {
  const u = c.usage;
  if (!u) return c.aiEstimated ? 'No usage recorded; estimated from the measured calls' : undefined;
  return [
    ...(u.source === 'transcript' ? ['Estimated from the transcript (the bot did not record usage yet)'] : []),
    `${u.llmModel ?? 'LLM'} (${u.llmServiceTier ?? 'default'}): ${u.llmInputTokens.toLocaleString()} in ` +
      `(${u.llmCachedInputTokens.toLocaleString()} cached), ${u.llmOutputTokens.toLocaleString()} out, ${u.llmRequests} requests`,
    `${u.sttModel ?? 'STT'}: ${u.sttSeconds}s reported`,
    `${u.ttsModel ?? 'TTS'}: ${u.ttsCharacters.toLocaleString()} characters`,
  ].join('\n');
}

const LINE_TYPE_LABELS: Record<string, string> = {
  mobile: 'mobile',
  landline: 'landline',
  fixedVoip: 'VoIP',
  nonFixedVoip: 'VoIP (mobile)',
  tollFree: 'toll-free',
  unknown: 'unknown',
};

export function lineLabel(type: string | null): string {
  return type ? LINE_TYPE_LABELS[type] || type : '—';
}

/** The LLM a call ran on and its processing tier, e.g. "gpt-5.5 · priority". */
export function llmLabel(u: PnlCall['usage']): string {
  if (!u?.llmModel) return '—';
  const tier = !u.llmServiceTier || u.llmServiceTier === 'default' ? 'standard' : u.llmServiceTier;
  return `${u.llmModel} · ${tier}`;
}

function Card({ value, label }: { value: string; label: string }) {
  return (
    <div className="admin-card">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function AdminPnl() {
  const [params, setParams] = useSearchParams();
  const days = WINDOWS.includes(Number(params.get('days'))) ? Number(params.get('days')) : 30;
  const [report, setReport] = useState<PnlReport | null>(null);
  const [error, setError] = useState('');
  const [chargedOnly, setChargedOnly] = useState(false);
  const [reloads, setReloads] = useState(0);
  // The call whose transcript is open under its row, and the ones loaded so far.
  const [openCall, setOpenCall] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<Record<string, TranscriptMessage[] | string>>({});

  const toggleTranscript = (callId: string) => {
    const next = openCall === callId ? null : callId;
    setOpenCall(next);
    if (next && !(next in transcripts)) {
      adminApi
        .transcript(next)
        .then((r) => setTranscripts((t) => ({ ...t, [next]: r.transcript })))
        .catch((e) =>
          setTranscripts((t) => ({ ...t, [next]: e instanceof Error ? e.message : 'Failed to load transcript' }))
        );
    }
  };

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError('');
    adminApi
      .pnl(days)
      .then((r) => !cancelled && setReport(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load the P&L'));
    return () => {
      cancelled = true;
    };
  }, [days, reloads]);

  const setDays = (d: number) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'pnl');
      next.set('days', String(d));
      return next;
    });

  const t = report?.totals;
  const a = report?.assumptions;
  const rows = report ? (chargedOnly ? report.calls.filter((c) => c.creditDeducted) : report.calls) : [];

  return (
    <div className="admin-section" style={{ marginTop: 0 }}>
      <div className="admin-tabs" style={{ marginBottom: 16, alignItems: 'center' }}>
        {WINDOWS.map((d) => (
          <button key={d} className={`admin-inline-btn ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>
            {d === 365 ? '1 year' : `${d} days`}
          </button>
        ))}
        <button className="admin-inline-btn" onClick={() => setReloads((n) => n + 1)} disabled={!report}>
          Reload
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {!report || !t || !a ? (
        !error && <div className="admin-muted">Loading… (the first load fetches prices from Twilio and Stripe)</div>
      ) : (
        <>
          <div className="admin-cards">
            <Card value={yen(t.revenue)} label={`Revenue from ${t.chargedCalls} charged calls`} />
            <Card value={yen(t.cost)} label={`Cost of ${t.calls} calls (${t.connectedMinutes} min)`} />
            <Card
              value={yen(t.profit)}
              label={`Profit${t.margin !== null ? ` · ${Math.round(t.margin * 100)}% margin` : ''}`}
            />
            <Card
              value={t.chargedCalls ? yen(t.profit / t.chargedCalls) : '—'}
              label="Profit per charged call"
            />
            <Card value={yen(t.cashCollected)} label="Payments in this window (net of tax)" />
          </div>

          <div className="admin-breakdowns">
            <div className="admin-breakdown">
              <h3>Cost per call</h3>
              {(
                [
                  ['Twilio', t.twilio],
                  ['LLM', t.llm],
                  ['Deepgram', t.stt],
                  ['Cartesia', t.tts],
                  ['Stripe fees', t.stripeFee],
                ] as [string, number][]
              ).map(([label, v]) => (
                <div className="row" key={label}>
                  <span>{label}</span>
                  <span>
                    {yen(v)}
                    <span className="admin-muted"> ({t.cost ? Math.round((v / t.cost) * 100) : 0}%)</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="admin-breakdown">
              <h3>Twilio by line type</h3>
              {Object.entries(t.byLineType)
                .sort((x, y) => y[1].twilio - x[1].twilio)
                .map(([type, v]) => (
                  <div className="row" key={type}>
                    <span>
                      {lineLabel(type)} <span className="admin-muted">({v.calls} calls, {v.billedMinutes} min)</span>
                    </span>
                    <span>
                      {yen(v.twilio)}
                      {v.perMinute !== null && <span className="admin-muted"> · {yen(v.perMinute)}/min</span>}
                    </span>
                  </div>
                ))}
            </div>
            <div className="admin-breakdown">
              <h3>Not in the calls</h3>
              <div className="row">
                <span title="Twilio's account total for these days minus the calls' prices: numbers, fees, calls made outside the app">
                  Other Twilio charges
                </span>
                <span>{yen(t.twilioOther)}</span>
              </div>
              <div className="row">
                <span title="Payments in the window minus revenue earned by calls: credits bought but not used (yet)">
                  Paid, not yet used
                </span>
                <span>{yen(t.cashCollected - t.revenue)}</span>
              </div>
              <div className="row">
                <span>Fixed (Cloud Run, plans)</span>
                <span className="admin-muted">not counted</span>
              </div>
            </div>
          </div>

          {(t.twilioPending > 0 || t.aiEstimated > 0 || t.aiUnknown > 0 || t.revenuePending > 0 ||
            a.unpricedModels.length > 0 || !report.stripeConfigured || !report.twilioConfigured) && (
            <div className="admin-notice">
              {!report.twilioConfigured && <div>Twilio credentials are missing on the server.</div>}
              {!report.stripeConfigured && <div>STRIPE_SECRET_KEY is missing on the server.</div>}
              {t.twilioPending > 0 && (
                <div>
                  {t.twilioPending} call{t.twilioPending === 1 ? ' has' : 's have'} no Twilio price yet (counted as ¥0).
                  Twilio prices a call a few minutes after it ends; reload to fetch more.
                </div>
              )}
              {t.revenuePending > 0 && (
                <div>{t.revenuePending} charged calls have no revenue: Stripe didn't return the payment's amount.</div>
              )}
              {t.aiEstimated > 0 && (
                <div>
                  {t.aiEstimated} calls predate usage recording; their AI cost is estimated from their transcripts,
                  or from the other calls' cost per minute when there is none (marked est.).
                </div>
              )}
              {t.aiUnknown > 0 && (
                <div>{t.aiUnknown} calls have no AI cost: no usage recorded and no measured calls to estimate from.</div>
              )}
              {a.unpricedModels.length > 0 && (
                <div>No price for {a.unpricedModels.join(', ')}; counted as ¥0.</div>
              )}
            </div>
          )}

          <details style={{ margin: '12px 0 20px', fontSize: 13 }} className="admin-muted">
            <summary style={{ cursor: 'pointer' }}>Prices used</summary>
            <div style={{ marginTop: 8, lineHeight: 1.7 }}>
              <div>$1 = ¥{a.usdJpy} · Stripe fee {(a.stripeFeeRate * 100).toFixed(1)}% of the amount charged</div>
              {Object.entries(a.llmPerMillionTokens).map(([model, p]) => (
                <div key={model}>
                  {model}: ${p.input} input, ${p.cachedInput} cached, ${p.output} output per 1M tokens
                </div>
              ))}
              <div>
                Deepgram:{' '}
                {Object.entries(a.deepgramPerMinute)
                  .map(([m, p]) => `${m} $${p}/min`)
                  .join(', ')}
              </div>
              <div>Cartesia: ${a.cartesiaPerMillionChars} per 1M characters</div>
              <div>
                A credit is worth what the user paid net of tax divided by the credits it bought (12 months of credits
                for an annual plan), from their latest payment before the call; free credits earn ¥0.
              </div>
            </div>
          </details>

          <label className="admin-muted" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
            <input type="checkbox" checked={chargedOnly} onChange={(e) => setChargedOnly(e.target.checked)} /> Only
            charged calls
          </label>

          {rows.length === 0 ? (
            <div className="admin-muted">No calls in this window.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table static">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>User</th>
                    <th>Callee</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Line</th>
                    <th>Length</th>
                    <th>Credit</th>
                    <th>Revenue</th>
                    <th>Twilio</th>
                    <th>¥/min</th>
                    <th>Model</th>
                    <th>LLM</th>
                    <th>Deepgram</th>
                    <th>Cartesia</th>
                    <th>Stripe</th>
                    <th>Profit</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <Fragment key={c.callId}>
                      <tr>
                        <td>
                          <Link to={`/admin/calls/${c.callId}`}>{fmtAdminDateTime(c.initiatedAt)}</Link>
                        </td>
                        <td>
                          {c.userId ? <Link to={`/admin/users/${c.userId}`}>{c.email || c.userId}</Link> : '—'}
                        </td>
                        <td>{c.toNumber || '—'}</td>
                        <td>{c.status || '—'}</td>
                        <td>{c.source || '—'}</td>
                        <td title={c.carrier || undefined}>{lineLabel(c.lineType)}</td>
                        <td>{fmtDuration(c.duration)}</td>
                        <td>{c.creditDeducted ? c.plan : <span className="admin-muted">{c.reason || 'not charged'}</span>}</td>
                        <td>{yen(c.revenue)}</td>
                        <td>{c.twilio === null ? <span className="admin-muted">pending</span> : yen(c.twilio)}</td>
                        <td>{yen(c.twilioPerMinute)}</td>
                        <td>{llmLabel(c.usage)}</td>
                        {(['llm', 'stt', 'tts'] as const).map((k) => (
                          <td key={k} title={usageTitle(c)}>
                            {yen(c[k])}
                            {c.aiEstimated && <span className="admin-muted"> est.</span>}
                          </td>
                        ))}
                        <td>{yen(c.stripeFee)}</td>
                        <td>
                          <Profit value={c.profit} />
                        </td>
                        <td>
                          {c.hasTranscript && (
                            <button className="admin-inline-btn" onClick={() => toggleTranscript(c.callId)}>
                              {openCall === c.callId ? 'Hide transcript' : 'Transcript'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {openCall === c.callId && (
                        <tr>
                          <td colSpan={TABLE_COLUMNS} className="wrap">
                            {!(c.callId in transcripts) ? (
                              <div className="admin-muted">Loading transcript…</div>
                            ) : typeof transcripts[c.callId] === 'string' ? (
                              <div className="admin-error">{transcripts[c.callId] as string}</div>
                            ) : (
                              <TranscriptMessages messages={transcripts[c.callId] as TranscriptMessage[]} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminPnl;
